import type { Order } from "../../lib/types.ts";
import { ApiFehler } from "../../api/basis.ts";
import { bestellungLaden, hubZeileLaden } from "../../api/bestellungen.ts";
import {
  abgeleiteteKennung,
  istAbgeleiteteKennung,
  jtlBestellungLaden,
  nummerAusAbgeleiteterKennung,
  zusammenfuehren,
} from "../../api/jtlBestellungen.ts";

/**
 * Kennungsauflösung für Adresse und Drawer; Reviewhistorie:
 * docs/superpowers/specs/2026-10-12-app-fachliche-schnitte-design.md.
 */
/** Die Form einer UUID - die eine Quelle fuer Adresse, Drawer, Historie und Aktivitaetenseite. */
export const UUID_MUSTER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** UUID -> id; sonst erste exakte Bestellnummer vor id. Mehrfachtreffer bleiben wie bisher. */
export function bestellungNachKennung(bestellungen: Order[], kennung: string): Order | undefined {
  if (UUID_MUSTER.test(kennung)) return bestellungen.find((b) => String(b.id) === kennung);
  // JTL-Bestellnummern sind nicht garantiert eindeutig (migration/zuordnung.ts).
  // Der bestehende Drawer-Abgleich behält bei Mehrfachtreffern den ersten.
  return (
    bestellungen.find((b) => String(b.orderNo ?? "") === kennung) ??
    bestellungen.find((b) => String(b.id) === kennung)
  );
}

/**
 * Der Ausgang einer Auflösung (Fehleranzeige Paket 2, Task 7). Früher lieferte sie `Order | null`, und `null`
 * hieß zweierlei: „es gibt keine Bestellung dazu“ UND „das Laden ist gescheitert“ – ein Netz- oder 5xx-Fehler
 * erschien dem Nutzer als „Bestellung X wurde nicht gefunden“. Jetzt ist es dreierlei:
 * - `treffer`: die Bestellung ist da. `hubFehler` trägt die Ursache, wenn nur der Hub-Teil (Archivstand, Aufgaben,
 *   Zahlungen) nicht kam – die Bestellung öffnet dann mit den JTL-Daten, der Aufrufer meldet den Mangel.
 * - `keinTreffer`: der Server kennt die Kennung nicht (404). NUR das ist „nicht gefunden“.
 * - `ladefehler`: das Laden ist gescheitert (Netz, 5xx, abgerissene Antwort …); `ursache` ist das Fehlerobjekt.
 */
export type AufloesungsErgebnis =
  | { art: "treffer"; bestellung: Order; hubFehler?: unknown }
  | { art: "keinTreffer" }
  | { art: "ladefehler"; ursache: unknown };

/** Ein 404 der JTL-Route: die Kennung gibt es nicht. Jeder andere Fehler ist ein Ladefehler, kein „kein Treffer“. */
function istKeinTreffer(fehler: unknown): boolean {
  return fehler instanceof ApiFehler && fehler.status === 404;
}

/**
 * Gezielter Nachladeweg auch für archivierte Bestellungen außerhalb des Stores.
 * Abgeleitete JTL-ID lädt nur JTL, UUID den bestehenden Hub-/JTL-Weg, Bestellnummer
 * JTL mit zusätzlicher Hub-Zeile (Archiv, Aufgaben, Zahlungen). Die Hub-Anreicherung
 * darf scheitern (`treffer` mit `hubFehler`); scheitert der Hauptabruf, ist das ein `ladefehler`,
 * außer der Server antwortet 404 (`keinTreffer`).
 *
 * UUID-Zweig: `bestellungLaden` liefert `null` bei einem 404 der Hub-Zeile (`keinTreffer`) und lehnt ab, wenn
 * irgendein Teil scheitert – auch bei einem 404 der JTL-Anreicherung. Die Hub-Zeile gibt es dann, „nicht gefunden“
 * wäre falsch: jede Ablehnung ist dort ein `ladefehler`.
 */
export async function bestellungNachKennungNachladen(kennung: string): Promise<AufloesungsErgebnis> {
  if (UUID_MUSTER.test(kennung)) {
    try {
      const bestellung = await bestellungLaden(kennung);
      return bestellung === null ? { art: "keinTreffer" } : { art: "treffer", bestellung };
    } catch (ursache) {
      return { art: "ladefehler", ursache };
    }
  }
  try {
    if (istAbgeleiteteKennung(kennung)) {
      // Die Nummer kommt von `nummerAusAbgeleiteterKennung` (EIN Ort fuer Praefix und Muster, `api/jtlBestellungen.ts`).
      // `null` heisst hier: die Nummer ist keine sichere ganze Zahl. Eine gerundete Nummer wuerde an JTL eine ANDERE
      // Bestellung adressieren, und eine so grosse Nummer gibt es dort nicht - also "kein Treffer", ohne Anfrage.
      const nummer = nummerAusAbgeleiteterKennung(kennung);
      if (nummer === null) return { art: "keinTreffer" };
      return { art: "treffer", bestellung: zusammenfuehren(await jtlBestellungLaden(nummer)) };
    }
    const jtl = await jtlBestellungLaden(kennung);
    let hub: Order | undefined;
    let hubGescheitert = false;
    let hubFehler: unknown;
    try {
      hub = (await hubZeileLaden(abgeleiteteKennung(jtl.kLieferantenBestellung))) ?? undefined;
    } catch (fehler) {
      hubGescheitert = true;
      hubFehler = fehler;
    }
    const bestellung = zusammenfuehren(jtl, hub);
    return hubGescheitert ? { art: "treffer", bestellung, hubFehler } : { art: "treffer", bestellung };
  } catch (ursache) {
    return istKeinTreffer(ursache) ? { art: "keinTreffer" } : { art: "ladefehler", ursache };
  }
}
